import { AccountCreateLambdaResponse } from "./accountActivationTypes";
import type { TFunction } from "i18next";

// hardcoded $ sign for now
export const formatCurrencyStr = (str: string | null): string =>
  str !== null && str !== "" ? formatCurrency(parseFloat(str)) : "Error";

const formatCurrency = (amt: number): string => {
  return `$${amt.toFixed(2)}`;
};

/**
 *
 */

export const determineIfWasFreeTrial = (
  createAccountResp: AccountCreateLambdaResponse,
): boolean => {
  const progPlanSubscriptionCost =
    createAccountResp.order.program_plan_obj.subscription_cost;

  const subShopifyProduct = createAccountResp.shopifyOrder.line_items.find(
    (li) => li.is_subscription_product === true,
  );

  if (!subShopifyProduct) {
    return false;
  }

  // not very confident that `parseFloat === 0.0` will work lol
  const subWasFree =
    Math.abs(parseFloat(subShopifyProduct.product_cost)) < 0.01;
  // if it's SUPPOSED to cost $$$ (according to program plan) but it ACTUALLY was free
  return subWasFree && progPlanSubscriptionCost > 0;
};

/**
 *
 */

interface HumanReadablePrices {
  subtotal: string;
  shipping: string;
  total: string;
  tax: string;
  totalAfterFreeTrial: undefined | string;
}

export const extractPricesToHumanReadable = (
  createAccountResp: AccountCreateLambdaResponse,
  isFreeTrial: boolean,
): HumanReadablePrices => {
  const {
    subtotal_price: subtotal,
    total_shipping_price_set: shippingPriceSet,
    total_tax: totalTax,
    total_price: totalPrice,
  } = createAccountResp.shopifyOrder;

  const progPlanCost =
    createAccountResp.order.program_plan_obj.subscription_cost;

  const totalPriceNum = parseFloat(totalPrice);

  return {
    subtotal: formatCurrencyStr(
      isFreeTrial ? progPlanCost.toString() : subtotal,
    ),
    // Hacky fix for MW-6245: Covers edge case where there are no discount_codes (applied by customer at checkout) but have discount_applications (applied to order, ex: automatic shipping discount)
    // According to Shopify docs: total_price is sum of all line item prices, discounts, shipping, taxes, and tips in the shop currency
    // Therefore, if total price is 0, we can assume shipping is also 0, even if the shipping price set doesn't reflect that
    // TODO: Ideally we get discount_applications from Shopify, but crunched on time
    shipping:
      totalPriceNum <= 0
        ? "$0.00"
        : formatCurrencyStr(shippingPriceSet.shop_money.amount),
    tax: formatCurrencyStr(totalTax),
    total: formatCurrencyStr(totalPrice),
    totalAfterFreeTrial: isFreeTrial ? formatCurrency(progPlanCost) : undefined,
  };
};

/**
 *
 */

export const getRenewalString = (
  accountCreateResp: AccountCreateLambdaResponse,
  t: TFunction,
  isDetailed: boolean,
): string => {
  const {
    subscription_freq_months: subscriptionFreqMonths,
    is_recurring: isRecurring,
    tag: programPlanTag,
  } = accountCreateResp.order.program_plan_obj;

  const autoRenewsPart = isDetailed ? "subAutoRenews" : "autoRenews";

  // Checking specifically if BH tag because sub freq months will be 0 but we don't want to display text for lifetimeAccess
  if (programPlanTag === "BETTERHELP") {
    return t(`startPage.orderSummaryCard.betterHelpAccess`);
  }

  if (programPlanTag === "ATB") {
    return t(`startPage.orderSummaryCard.atbAccess`);
  }

  const pluralSuffix = subscriptionFreqMonths > 1 ? "Plural" : "";

  if (isRecurring) {
    return t(`startPage.orderSummaryCard.${autoRenewsPart}${pluralSuffix}`, {
      numMonths: subscriptionFreqMonths,
    });
  } else if (subscriptionFreqMonths > 0) {
    return t(
      `startPage.orderSummaryCard.nonRecurringSubscribedFor${pluralSuffix}`,
      { numMonths: subscriptionFreqMonths },
    );
  } else {
    return t(`startPage.orderSummaryCard.lifetimeAccess`);
  }
};
